import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import PageHero from "@/components/PageHero";
import CtaBand from "@/components/CtaBand";
import { Reveal } from "@/components/Reveal";
import api from "@/lib/api";

export default function Insights() {
  const [articles, setArticles] = useState(null);
  const [category, setCategory] = useState("All");

  useEffect(() => {
    api.get("/articles").then((r) => setArticles(r.data)).catch(() => setArticles([]));
  }, []);

  const SLUG_ORDER = [
    "what-influenced-the-arms-ltd-approach",
    "understanding-a-culture-is-simple-if-you-ask-the-right-questions-of-the-right-people",
    "how-complacent-attitudes-form-and-why",
    "culture-is-the-collective-behaviour-of-everyone-in-the-organisation",
  ];
  const ordered = [...(articles || [])].sort((a, b) => {
    const ia = SLUG_ORDER.indexOf(a.slug);
    const ib = SLUG_ORDER.indexOf(b.slug);
    if (ia === -1 && ib === -1) return 0;
    if (ia === -1) return 1;
    if (ib === -1) return -1;
    return ia - ib;
  });
  const categories = ["All", ...new Set(ordered.map((a) => a.category))];
  const filtered = ordered.filter((a) => category === "All" || a.category === category);
  const [featured, ...rest] = filtered;

  return (
    <div data-testid="insights-page">
      <PageHero
        eyebrow="Insights & articles"
        title="Thinking that challenges thinking"
        description="Perspectives on safety culture, behaviour change and risk — from the behavioural scientists and  consultant-actors who do the work."
      />

      <section className="bg-slate-50 py-20 lg:py-28">
        <div className="container-arms">
          {articles === null ? (
            <p className="text-slate-500" data-testid="insights-loading">Loading articles…</p>
          ) : articles.length === 0 ? (
            <div className="rounded-3xl border border-dashed border-slate-300 bg-white p-16 text-center" data-testid="insights-empty">
              <h2 className="text-xl sm:text-2xl font-bold text-navy">Articles coming soon</h2>
              <p className="mt-3 text-slate-500">Our first insights are being written. Check back shortly — or subscribe via the contact form.</p>
            </div>
          ) : (
            <>
              <div className="mb-10 flex flex-wrap gap-2" role="group" aria-label="Filter articles by category">
                {categories.map((c) => (
                  <button
                    key={c}
                    onClick={() => setCategory(c)}
                    data-testid={`insights-filter-${c.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                    className={`rounded-full px-5 py-2 text-sm font-semibold transition-colors ${
                      category === c ? "bg-navy text-white" : "bg-white text-slate-600 border border-slate-200 hover:border-teal"
                    }`}
                  >
                    {c}
                  </button>
                ))}
              </div>

              {featured && (
                <Reveal>
                  <Link
                    to={`/insights/${featured.slug}`}
                    data-testid="insights-featured-article"
                    className="group mb-12 grid overflow-hidden rounded-3xl border border-slate-200 bg-white transition-shadow duration-300 hover:shadow-xl hover:shadow-slate-200 lg:grid-cols-2"
                  >
                    {featured.cover_image && (
                      <div className="overflow-hidden">
                        <img src={featured.cover_image} alt="" className="h-full w-full object-cover img-duotone transition-transform duration-500 group-hover:scale-105" />
                      </div>
                    )}
                    <div className="flex flex-col justify-center p-8 lg:p-12">
                      <p className="eyebrow mb-4">{featured.category}</p>
                      <h2 className="text-2xl sm:text-3xl font-bold tracking-tight text-navy group-hover:text-teal transition-colors">{featured.title}</h2>
                      <p className="mt-4 text-sm sm:text-base text-slate-600 leading-relaxed">{featured.excerpt}</p>
                      <span className="mt-6 inline-flex items-center gap-2 text-sm font-semibold text-teal">
                        Read article <ArrowRight size={16} className="transition-transform duration-300 group-hover:translate-x-1" />
                      </span>
                    </div>
                  </Link>
                </Reveal>
              )}

              <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
                {rest.map((a, i) => (
                  <Reveal key={a.id} delay={i * 0.06}>
                    <Link
                      to={`/insights/${a.slug}`}
                      data-testid={`insights-article-${a.slug}`}
                      className="group flex h-full flex-col overflow-hidden rounded-3xl border border-slate-200 bg-white transition-transform duration-300 hover:-translate-y-1"
                    >
                      {a.cover_image && (
                        <div className="overflow-hidden">
                          <img src={a.cover_image} alt="" loading="lazy" className="aspect-[16/10] w-full object-cover img-duotone transition-transform duration-500 group-hover:scale-105" />
                        </div>
                      )}
                      <div className="flex flex-1 flex-col p-7">
                        <p className="eyebrow mb-3">{a.category}</p>
                        <h3 className="text-lg sm:text-xl font-bold tracking-tight text-navy group-hover:text-teal transition-colors">{a.title}</h3>
                        <p className="mt-3 flex-1 text-sm text-slate-600 leading-relaxed">{a.excerpt}</p>
                        <span className="mt-5 inline-flex items-center gap-2 text-sm font-semibold text-teal">
                          Read article <ArrowRight size={15} className="transition-transform duration-300 group-hover:translate-x-1" />
                        </span>
                      </div>
                    </Link>
                  </Reveal>
                ))}
              </div>
            </>
          )}
        </div>
      </section>

      <CtaBand title="Have a challenge you would like to share?" text="The best insights come from real organisations. Start a conversation about yours." />
    </div>
  );
}
